import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import endpoints from "../../helpers/endpoints";
import fetch from "../../utils/fetch";
import { getAdminHeaders } from "../../utils/adminAuth";
import styles from "./QrScanMonitor.module.scss";

/** Landing công khai khi người dùng nhấp Dynamic ID trong bảng */
const PUBLIC_QR_CODE_BASE_URL = (
  typeof process.env.REACT_APP_PUBLIC_QR_CODE_URL === "string" && process.env.REACT_APP_PUBLIC_QR_CODE_URL.trim()
    ? process.env.REACT_APP_PUBLIC_QR_CODE_URL.trim().replace(/\/$/, "")
    : "https://code.nineqr.com"
);

function publicQrCodeUrl(dynamicId: string): string {
  return `${PUBLIC_QR_CODE_BASE_URL}/${encodeURIComponent(dynamicId)}`;
}

type DimItem = { value: string; scanCount: number };
type Dimensions = {
  country: DimItem[];
  region: DimItem[];
  city: DimItem[];
  device: DimItem[];
  os: DimItem[];
  browser: DimItem[];
  ads_state: DimItem[];
  uniqueScanCount: number;
};

type Summary = {
  scanCount: number;
  adsScanCount?: number;
  noAdsScanCount?: number;
  unknownAdsScanCount?: number;
  qrCodeCount: number;
  userCount: number;
  topDomain?: { scanDomain: string; scanCount: number; adsScanCount?: number; noAdsScanCount?: number; unknownAdsScanCount?: number } | null;
};

type HourlyRow = {
  hour: string;
  scanDomain: string;
  scanCount: number;
  adsScanCount?: number;
  noAdsScanCount?: number;
  unknownAdsScanCount?: number;
};

type CreateGeo = { country?: string; region?: string; city?: string } | null;

type TopCode = {
  dynamicId: string;
  ownerUserId: string;
  scanCount: number;
  adsScanCount?: number;
  noAdsScanCount?: number;
  unknownAdsScanCount?: number;
  lastScanHour: string;
  domains: string[];
  createGeo?: CreateGeo;
};

type QrCreatedRow = {
  hour: string;
  productId: string;
  contentType: string;
  ownershipStatus: string;
  qrCodeCount: number;
  dynamicCount: number;
  staticCount: number;
};

type CreatedDimItem = { value: string; qrCodeCount: number };
type CreatedDimensions = {
  country: CreatedDimItem[];
  region: CreatedDimItem[];
  city: CreatedDimItem[];
};

type CreatedCode = {
  dynamicId: string;
  ownerUserId: string;
  scanCount: number;
  productId: string;
  contentType: string;
  ownershipStatus: string;
  workPageId: string;
  createdAt: string;
  createGeo?: CreateGeo;
};

type CodeListMode = "scan" | "created";

type UserRegistrationRow = {
  hour: string;
  userCount: number;
};

type Detail = {
  code: {
    dynamicId: string;
    ownerUserId: string;
    ownershipStatus: string;
    contentType: string;
    destinationUrl: string;
    codeRedirect: string;
    productId?: string;
    scanCounter: number;
    enabled: boolean;
    active: boolean;
    createdAt?: string;
    updatedAt?: string;
  } | null;
  hourly: HourlyRow[];
  domains: { scanDomain: string; scanCount: number; adsScanCount?: number; noAdsScanCount?: number; unknownAdsScanCount?: number }[];
};

function toInputDateTime(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function formatNumber(value: number | undefined) {
  return new Intl.NumberFormat("vi-VN").format(value || 0);
}

/** ISO-3166 alpha-2 (vd "vn") → emoji cờ bằng regional indicator symbols. */
function countryFlagEmoji(code: string | undefined | null): string {
  const cc = (code || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  const base = 0x1f1e6; // 🇦
  return String.fromCodePoint(base + (cc.charCodeAt(0) - 65), base + (cc.charCodeAt(1) - 65));
}

/** Cột "Quốc gia tạo": cờ + mã ISO viết hoa, hoặc "-" nếu chưa có createGeo. */
function CreateCountryCell({ geo }: { geo?: CreateGeo }) {
  const code = geo?.country?.trim();
  if (!code) return <span className={styles.Muted}>-</span>;
  const flag = countryFlagEmoji(code);
  const title = [geo?.city, geo?.region, code.toUpperCase()].filter(Boolean).join(", ");
  return (
    <span className={styles.CreateGeoCell} title={title}>
      {flag && <span className={styles.FlagEmoji}>{flag}</span>}
      <span>{code.toUpperCase()}</span>
    </span>
  );
}

function createdTypeLabel(value: string) {
  const [productId, contentType] = value.split(" / ");
  const productLabels: Record<string, string> = {
    "dynamic-code": "Dynamic",
    "static-code": "Static",
    unknown: "Chưa xác định",
  };
  const productLabel = productLabels[productId] || productId;
  const contentLabel = contentType === "unknown" ? "Chưa xác định" : contentType;
  return { productLabel, contentLabel };
}

function formatHour(value: string) {
  if (!value) return "";
  return new Date(value).toLocaleString("vi-VN", {
    hour: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });
}

function formatHourLabel(value: string) {
  if (!value) return "";
  return new Date(value).toLocaleString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateTime(value: string | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const QrScanMonitor = () => {
  const [from, setFrom] = useState(() => toInputDateTime(new Date(Date.now() - 24 * 60 * 60 * 1000)));
  const [to, setTo] = useState(() => toInputDateTime(new Date()));
  const [domain, setDomain] = useState("");
  const [dynamicId, setDynamicId] = useState("");
  const [userId, setUserId] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [adsState, setAdsState] = useState("");
  const [workPageId, setWorkPageId] = useState("");
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);

  const [summary, setSummary] = useState<Summary | null>(null);
  const [hourly, setHourly] = useState<HourlyRow[]>([]);
  const [domains, setDomains] = useState<{ scanDomain: string; scanCount: number; adsScanCount?: number; noAdsScanCount?: number; unknownAdsScanCount?: number }[]>([]);
  const [topCodes, setTopCodes] = useState<TopCode[]>([]);
  const [createdCodes, setCreatedCodes] = useState<CreatedCode[]>([]);
  const [codeListMode, setCodeListMode] = useState<CodeListMode>("scan");
  const [qrCreatedHourly, setQrCreatedHourly] = useState<QrCreatedRow[]>([]);
  const [createdDimensions, setCreatedDimensions] = useState<CreatedDimensions | null>(null);
  const [userRegistrationHourly, setUserRegistrationHourly] = useState<UserRegistrationRow[]>([]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [selectedDynamicId, setSelectedDynamicId] = useState("");
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [assignEmail, setAssignEmail] = useState("");
  const [assignGuestId, setAssignGuestId] = useState("");
  const [assignGuestIdHash, setAssignGuestIdHash] = useState("");
  const [assignDynamicIds, setAssignDynamicIds] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignMessage, setAssignMessage] = useState("");
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [liveEndTime, setLiveEndTime] = useState(true);

  const query = useMemo(() => {
    const q: any = {
      from: new Date(from).toISOString(),
      to: new Date(to).toISOString(),
    };
    if (domain.trim()) q.domain = domain.trim();
    if (dynamicId.trim()) q.dynamicId = dynamicId.trim();
    if (userId.trim()) q.userId = userId.trim();
    if (country.trim()) q.country = country.trim();
    if (region.trim()) q.region = region.trim();
    if (adsState.trim()) q.adsState = adsState.trim();
    if (workPageId.trim()) q.workPageId = workPageId.trim();
    return q;
  }, [from, to, domain, dynamicId, userId, country, region, adsState, workPageId]);

  const hourlyTotals = useMemo(() => {
    const map = new Map<string, { hour: string; scanCount: number; adsScanCount: number; noAdsScanCount: number; unknownAdsScanCount: number }>();
    hourly.forEach((row) => {
      const current = map.get(row.hour) || { hour: row.hour, scanCount: 0, adsScanCount: 0, noAdsScanCount: 0, unknownAdsScanCount: 0 };
      current.scanCount += row.scanCount || 0;
      current.adsScanCount += row.adsScanCount || 0;
      current.noAdsScanCount += row.noAdsScanCount || 0;
      current.unknownAdsScanCount += row.unknownAdsScanCount || 0;
      map.set(row.hour, current);
    });
    return Array.from(map.values()).sort((a, b) => a.hour.localeCompare(b.hour));
  }, [hourly]);

  const maxHourly = Math.max(...hourlyTotals.map((row) => row.scanCount), 1);

  const qrCreatedTotals = useMemo(() => {
    const map = new Map<string, { hour: string; qrCodeCount: number; dynamicCount: number; staticCount: number }>();
    qrCreatedHourly.forEach((row) => {
      const current = map.get(row.hour) || { hour: row.hour, qrCodeCount: 0, dynamicCount: 0, staticCount: 0 };
      current.qrCodeCount += row.qrCodeCount || 0;
      current.dynamicCount += row.dynamicCount || 0;
      current.staticCount += row.staticCount || 0;
      map.set(row.hour, current);
    });
    return Array.from(map.values()).sort((a, b) => a.hour.localeCompare(b.hour));
  }, [qrCreatedHourly]);

  const userRegistrationTotals = useMemo(() => {
    return [...userRegistrationHourly].sort((a, b) => a.hour.localeCompare(b.hour));
  }, [userRegistrationHourly]);

  const qrCreatedByType = useMemo(() => {
    const map = new Map<string, number>();
    qrCreatedHourly.forEach((row) => {
      const key = `${row.productId || "unknown"} / ${row.contentType || "unknown"}`;
      map.set(key, (map.get(key) || 0) + (row.qrCodeCount || 0));
    });
    return Array.from(map.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  }, [qrCreatedHourly]);

  const qrCreatedByCountry = useMemo(() => {
    const exactRows = createdDimensions?.country || [];
    if (exactRows.length > 0) {
      return exactRows.map((row) => ({
        country: row.value || "unknown",
        count: row.qrCodeCount || 0,
      }));
    }

    const map = new Map<string, number>();
    createdCodes.forEach((row) => {
      const key = row.createGeo?.country?.trim().toLowerCase() || "unknown";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([countryCode, count]) => ({ country: countryCode, count }))
      .sort((a, b) => b.count - a.count);
  }, [createdDimensions, createdCodes]);

  const totalQrCreated = qrCreatedTotals.reduce((sum, row) => sum + row.qrCodeCount, 0);
  const totalUserRegistered = userRegistrationTotals.reduce((sum, row) => sum + row.userCount, 0);
  const activeFilterCount = [domain, dynamicId, userId, country, region, adsState, workPageId]
    .filter((value) => value.trim()).length;
  const maxCreatedTypeCount = Math.max(...qrCreatedByType.map((row) => row.count), 1);
  const maxCreatedCountryCount = Math.max(...qrCreatedByCountry.map((row) => row.count), 1);
  const maxCreatedHourly = Math.max(
    ...qrCreatedTotals.map((row) => row.qrCodeCount),
    ...userRegistrationTotals.map((row) => row.userCount),
    1
  );

  function applyResponse<T>(res: any, setter: (value: T) => void, fallback: T, label: string, warnings: string[]) {
    if (res?.status >= 400) {
      warnings.push(`${label}: ${res?.body?.message || "Không tải được dữ liệu."}`);
      setter(fallback);
      return;
    }
    setter((res?.body?.result ?? fallback) as T);
  }

  async function loadData() {
    setLoading(true);
    setError("");
    setDetail(null);
    setSelectedDynamicId("");
    setDetailModalOpen(false);
    const requestQuery = { ...query };
    if (liveEndTime) {
      const now = new Date();
      requestQuery.to = now.toISOString();
      setTo(toInputDateTime(now));
    }
    try {
      const [summaryRes, hourlyRes, domainsRes, topCodesRes, qrCreatedRes, userRegistrationRes, dimensionsRes, createdCodesRes, createdDimensionsRes]: any = await Promise.allSettled([
        fetch(endpoints.QR_SCAN_SUMMARY, { query: requestQuery, headers: getAdminHeaders() }),
        fetch(endpoints.QR_SCAN_HOURLY, { query: requestQuery, headers: getAdminHeaders() }),
        fetch(endpoints.QR_SCAN_DOMAINS, { query: requestQuery, headers: getAdminHeaders() }),
        fetch(endpoints.QR_SCAN_TOP_CODES, { query: { ...requestQuery, limit: 50 }, headers: getAdminHeaders() }),
        fetch(endpoints.QR_CREATED_HOURLY, { query: requestQuery, headers: getAdminHeaders() }),
        fetch(endpoints.USER_REGISTRATION_HOURLY, { query: requestQuery, headers: getAdminHeaders() }),
        fetch(endpoints.QR_SCAN_DIMENSIONS, { query: requestQuery, headers: getAdminHeaders() }),
        fetch(endpoints.QR_CREATED_CODES, { query: { ...requestQuery, limit: 100 }, headers: getAdminHeaders() }),
        fetch(endpoints.QR_CREATED_DIMENSIONS, { query: { ...requestQuery, limit: 20 }, headers: getAdminHeaders() }),
      ]);

      const settledValues = [summaryRes, hourlyRes, domainsRes, topCodesRes, qrCreatedRes, userRegistrationRes, dimensionsRes, createdCodesRes, createdDimensionsRes].map((item) => (
        item.status === "fulfilled" ? item.value : { status: 500, body: { message: item.reason?.message || "Request failed" } }
      ));
      const warnings: string[] = [];

      applyResponse<Summary | null>(settledValues[0], setSummary, null, "Tổng quan scan", warnings);
      applyResponse<HourlyRow[]>(settledValues[1], setHourly, [], "Scan theo giờ", warnings);
      applyResponse<{ scanDomain: string; scanCount: number; adsScanCount?: number; noAdsScanCount?: number; unknownAdsScanCount?: number }[]>(settledValues[2], setDomains, [], "Scan theo domain", warnings);
      applyResponse<TopCode[]>(settledValues[3], setTopCodes, [], "Top QR", warnings);
      applyResponse<QrCreatedRow[]>(settledValues[4], setQrCreatedHourly, [], "QR tạo mới", warnings);
      applyResponse<UserRegistrationRow[]>(settledValues[5], setUserRegistrationHourly, [], "User đăng ký mới", warnings);
      applyResponse<Dimensions | null>(settledValues[6], setDimensions, null, "Thống kê theo chiều", warnings);
      applyResponse<CreatedCode[]>(settledValues[7], setCreatedCodes, [], "Danh sách QR tạo mới", warnings);
      applyResponse<CreatedDimensions | null>(settledValues[8], setCreatedDimensions, null, "QR tạo theo quốc gia", warnings);

      setError(warnings.length > 0 ? warnings.join(" | ") : "");
    } catch (err: any) {
      setError(err?.message || "Không tải được dữ liệu thống kê.");
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(code: string) {
    setSelectedDynamicId(code);
    setDetailLoading(true);
    setDetailError("");
    try {
      const res: any = await fetch(`${endpoints.QR_SCAN_CODE_DETAIL}/${encodeURIComponent(code)}`, {
        query,
        headers: getAdminHeaders(),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không tải được chi tiết QR.");
      }
      setDetail(res.body.result || null);
    } catch (err: any) {
      setDetail(null);
      setDetailError(err?.message || "Không tải được chi tiết QR.");
    } finally {
      setDetailLoading(false);
    }
  }

  async function openQrDetailModal(code: string) {
    setDetailModalOpen(true);
    await loadDetail(code);
  }

  function closeQrDetailModal() {
    setDetailModalOpen(false);
  }

  async function assignGuestCodes() {
    setAssigning(true);
    setError("");
    setAssignMessage("");
    try {
      const dynamicIds = assignDynamicIds
        .split(/[\s,]+/)
        .map((item) => item.trim())
        .filter(Boolean);
      const body: any = {
        email: assignEmail.trim(),
      };
      if (assignGuestId.trim()) body.guestId = assignGuestId.trim();
      if (assignGuestIdHash.trim()) body.guestIdHash = assignGuestIdHash.trim();
      if (dynamicIds.length > 0) body.dynamicIds = dynamicIds;

      const res: any = await fetch(endpoints.QR_ASSIGN_GUEST_CODES, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify(body),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không assign được QR guest.");
      }
      const result = res.body.result || {};
      setAssignMessage(`Đã assign ${formatNumber(result.assignedCount)} / ${formatNumber(result.matchedCount)} QR guest cho ${result.email || assignEmail.trim()}.`);
      if (result.assignedCount > 0) {
        loadData();
      }
    } catch (err: any) {
      setError(err?.message || "Không assign được QR guest.");
    } finally {
      setAssigning(false);
    }
  }

  useEffect(() => {
    loadData();
    // Load initial 24h once; filters are applied explicitly by the Lọc button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.ContentWrapper}>
      <div className={styles.Header}>
        <div className={styles.HeaderCopy}>
          <span className={styles.Eyebrow}>Analytics dashboard</span>
          <h1 className={styles.Title}>Giám sát QR Scan</h1>
          <p className={styles.Subtitle}>Theo dõi hiệu suất scan, tăng trưởng QR và hành vi người dùng trên một màn hình.</p>
        </div>
        <div className={styles.LoadingStatus} aria-live="polite">
          <span className={`${styles.StatusDot} ${loading ? styles.StatusDotLoading : ""}`} />
          <span>{loading ? "Đang cập nhật dữ liệu" : "Dữ liệu đã sẵn sàng"}</span>
          {loading && <CircularProgress size={18} />}
        </div>
      </div>

      <section className={`${styles.Panel} ${styles.FilterPanel}`}>
        <div className={styles.PanelHeadRow}>
          <div>
            <h2 className={styles.PanelTitle}>Bộ lọc dữ liệu</h2>
            <p className={styles.PanelHint}>Khoảng thời gian áp dụng cho toàn bộ số liệu bên dưới.</p>
          </div>
          {activeFilterCount > 0 && (
            <span className={styles.ActiveFilterBadge}>{activeFilterCount} bộ lọc đang dùng</span>
          )}
        </div>
        <div className={styles.FilterGrid}>
          <div className={styles.InputGroup}>
            <label>Từ thời điểm</label>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className={styles.InputGroup}>
            <label>Đến thời điểm</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setLiveEndTime(false);
              }}
            />
          </div>
          <div className={styles.InputGroup}>
            <label>Domain</label>
            <input value={domain} placeholder="code.qrcode-gen.com" onChange={(e) => setDomain(e.target.value)} />
          </div>
          <div className={styles.InputGroup}>
            <label>Dynamic ID</label>
            <input value={dynamicId} placeholder="R5GIZZFX" onChange={(e) => setDynamicId(e.target.value)} />
          </div>
        </div>

        {advancedFiltersOpen && (
          <div className={styles.AdvancedFilterGrid}>
            <div className={styles.InputGroup}>
              <label>User ID</label>
              <input value={userId} placeholder="Owner user id" onChange={(e) => setUserId(e.target.value)} />
            </div>
            <div className={styles.InputGroup}>
              <label>Quốc gia (ISO 2)</label>
              <input value={country} placeholder="vn, us, th" onChange={(e) => setCountry(e.target.value)} />
            </div>
            <div className={styles.InputGroup}>
              <label>Khu vực</label>
              <input value={region} placeholder="Hà Nội, Hồ Chí Minh" onChange={(e) => setRegion(e.target.value)} />
            </div>
            <div className={styles.InputGroup}>
              <label>Quảng cáo</label>
              <select value={adsState} onChange={(e) => setAdsState(e.target.value)}>
                <option value="">Tất cả</option>
                <option value="ads">Có ads</option>
                <option value="no_ads">Không ads</option>
              </select>
            </div>
            <div className={styles.InputGroup}>
              <label>WorkPage ID</label>
              <input value={workPageId} placeholder="Work page id" onChange={(e) => setWorkPageId(e.target.value)} />
            </div>
          </div>
        )}

        <div className={styles.FilterFooter}>
          <div className={styles.FilterUtilityActions}>
            <button
              type="button"
              className={styles.TextButton}
              aria-expanded={advancedFiltersOpen}
              onClick={() => setAdvancedFiltersOpen((value) => !value)}
            >
              {advancedFiltersOpen ? "Ẩn bộ lọc nâng cao" : "Mở bộ lọc nâng cao"}
            </button>
            <button
              type="button"
              className={liveEndTime ? styles.LiveTimeButtonActive : styles.TextButton}
              onClick={() => {
                setTo(toInputDateTime(new Date()));
                setLiveEndTime(true);
              }}
            >
              {liveEndTime ? "Đang theo thời gian thực" : "Đến thời điểm hiện tại"}
            </button>
          </div>
          <div className={styles.FilterActions}>
            <button
              type="button"
              className={styles.SecondaryButton}
              onClick={() => {
                setDomain("");
                setDynamicId("");
                setUserId("");
                setCountry("");
                setRegion("");
                setAdsState("");
                setWorkPageId("");
              }}
            >
              Xóa lọc
            </button>
            <button type="button" className={styles.PrimaryButton} disabled={loading} onClick={loadData}>
              {loading ? "Đang tải..." : "Áp dụng bộ lọc"}
            </button>
          </div>
        </div>
      </section>

      {error && <div className={styles.Error}>{error}</div>}
      {assignMessage && <div className={styles.Success}>{assignMessage}</div>}

      <details className={styles.ActionDisclosure}>
        <summary>
          <span>
            <strong>Assign QR guest cho user</strong>
            <small>Công cụ vận hành — mở khi cần gán QR ẩn danh</small>
          </span>
          <span className={styles.DisclosureAction}>Mở công cụ</span>
        </summary>
        <div className={styles.AssignGrid}>
          <div className={styles.InputGroup}>
            <label>Email user nhận QR</label>
            <input value={assignEmail} placeholder="user@example.com" onChange={(e) => setAssignEmail(e.target.value)} />
          </div>
          <div className={styles.InputGroup}>
            <label>Guest ID</label>
            <input value={assignGuestId} placeholder="guest_id cookie" onChange={(e) => setAssignGuestId(e.target.value)} />
          </div>
          <div className={styles.InputGroup}>
            <label>Guest ID Hash</label>
            <input value={assignGuestIdHash} placeholder="sha256 guest id" onChange={(e) => setAssignGuestIdHash(e.target.value)} />
          </div>
          <div className={styles.InputGroup}>
            <label>Dynamic IDs</label>
            <input value={assignDynamicIds} placeholder="QGDN8OXO, ABC12345" onChange={(e) => setAssignDynamicIds(e.target.value)} />
          </div>
          <button type="button" className={styles.PrimaryButton} disabled={assigning} onClick={assignGuestCodes}>
            {assigning ? "Đang assign..." : "Assign QR"}
          </button>
        </div>
      </details>

      <div className={styles.KpiGrid}>
        <div className={`${styles.KpiCard} ${styles.KpiCardPrimary}`}>
          <div className={styles.KpiLabel}>Tổng lượt scan</div>
          <div className={styles.KpiValue}>{formatNumber(summary?.scanCount)}</div>
          <div className={styles.KpiMeta}>Trong khoảng thời gian đã chọn</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>Scan có quảng cáo</div>
          <div className={styles.KpiValue}>{formatNumber(summary?.adsScanCount)}</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>Scan không quảng cáo</div>
          <div className={styles.KpiValue}>{formatNumber(summary?.noAdsScanCount)}</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>QR có scan</div>
          <div className={styles.KpiValue}>{formatNumber(summary?.qrCodeCount)}</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>User có scan</div>
          <div className={styles.KpiValue}>{formatNumber(summary?.userCount)}</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>Khách duy nhất</div>
          <div className={styles.KpiValue}>{formatNumber(dimensions?.uniqueScanCount)}</div>
          <div className={styles.KpiMeta}>Số liệu ước lượng</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>Top domain</div>
          <div className={styles.KpiValue}>{formatNumber(summary?.topDomain?.scanCount)}</div>
          <div className={styles.KpiMeta}>{summary?.topDomain?.scanDomain || "Chưa có dữ liệu"}</div>
        </div>
        <div className={`${styles.KpiCard} ${styles.KpiCardSuccess}`}>
          <div className={styles.KpiLabel}>User đăng ký mới</div>
          <div className={styles.KpiValue}>{formatNumber(totalUserRegistered)}</div>
        </div>
        <div className={`${styles.KpiCard} ${styles.KpiCardWarm}`}>
          <div className={styles.KpiLabel}>QR tạo mới</div>
          <div className={styles.KpiValue}>{formatNumber(totalQrCreated)}</div>
        </div>
      </div>

      <section className={styles.Panel}>
        <div className={styles.PanelHeadRow}>
          <div>
            <h2 className={styles.PanelTitle}>Phân bổ lượt scan</h2>
            <p className={styles.PanelHint}>Theo vị trí Cloudflare và thông tin User-Agent.</p>
          </div>
          <span className={styles.SectionMeta}>Vị trí · Thiết bị · Trình duyệt</span>
        </div>
        <div className={styles.DimGrid}>
          {([
            { key: "country", title: "Quốc gia" },
            { key: "region", title: "Khu vực" },
            { key: "city", title: "Thành phố" },
            { key: "device", title: "Thiết bị" },
            { key: "os", title: "Hệ điều hành" },
            { key: "browser", title: "Trình duyệt" },
          ] as { key: keyof Dimensions; title: string }[]).map(({ key, title }) => {
            const items = (dimensions?.[key] as DimItem[]) || [];
            return (
              <div className={styles.DimCol} key={key}>
                <h3 className={styles.DimTitle}>{title}</h3>
                {items.length === 0 && <span className={styles.Muted}>Chưa có dữ liệu.</span>}
                {items.slice(0, 8).map((item) => (
                  <div className={styles.DomainRow} key={item.value}>
                    <div className={styles.DomainName}>{item.value || "unknown"}</div>
                    <div className={styles.DomainCount}>{formatNumber(item.scanCount)}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>

      <div className={styles.GridTwo}>
        <section className={styles.Panel}>
          <div className={styles.PanelHeadRow}>
            <div>
              <h2 className={styles.PanelTitle}>Lượt scan theo giờ</h2>
              <p className={styles.PanelHint}>Di chuột vào cột để xem chi tiết.</p>
            </div>
            <span className={styles.TotalBadge}>{formatNumber(summary?.scanCount)} scan</span>
          </div>
          <div className={styles.Chart}>
            {hourlyTotals.length === 0 && <span className={styles.Muted}>Chưa có dữ liệu.</span>}
            {hourlyTotals.map((row) => (
              <div className={styles.BarWrap} key={row.hour}>
                <div className={styles.BarTrack}>
                  <div className={styles.Bar} style={{ height: `${Math.max((row.scanCount / maxHourly) * 135, 2)}px` }}>
                    <div className={styles.BarTooltip}>
                      <strong>{formatNumber(row.scanCount)}</strong>
                      <span>{formatHour(row.hour)}</span>
                      <span>Ads: {formatNumber(row.adsScanCount)}</span>
                      <span>No ads: {formatNumber(row.noAdsScanCount)}</span>
                      {(row.unknownAdsScanCount || 0) > 0 && <span>Chưa phân loại: {formatNumber(row.unknownAdsScanCount)}</span>}
                    </div>
                  </div>
                </div>
                <div className={styles.BarLabel}>{formatHourLabel(row.hour)}</div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.Panel}>
          <div className={styles.PanelHeadRow}>
            <div>
              <h2 className={styles.PanelTitle}>Scan theo domain</h2>
              <p className={styles.PanelHint}>Nguồn phát sinh lượt quét.</p>
            </div>
          </div>
          {domains.length === 0 && <span className={styles.Muted}>Chưa có dữ liệu.</span>}
          <div className={styles.CompactList}>
            {domains.slice(0, 12).map((row) => (
              <div className={styles.DomainRow} key={row.scanDomain}>
                <div className={styles.DomainName}>{row.scanDomain}</div>
                <div className={styles.DomainCount}>
                  {formatNumber(row.scanCount)}
                  <span className={styles.DomainMeta}>Ads {formatNumber(row.adsScanCount)} / No ads {formatNumber(row.noAdsScanCount)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className={styles.GridTwoEqual}>
        <section className={styles.Panel}>
          <div className={styles.PanelHeadRow}>
            <div>
              <h2 className={styles.PanelTitle}>User đăng ký mới</h2>
              <p className={styles.PanelHint}>Xu hướng đăng ký theo giờ.</p>
            </div>
            <span className={`${styles.TotalBadge} ${styles.TotalBadgeGreen}`}>
              {formatNumber(totalUserRegistered)} user
            </span>
          </div>
          <div className={styles.Chart}>
            {userRegistrationTotals.length === 0 && <span className={styles.Muted}>Chưa có dữ liệu.</span>}
            {userRegistrationTotals.map((row) => (
              <div className={styles.BarWrap} key={row.hour}>
                <div className={styles.BarTrack}>
                  <div className={styles.BarGreen} style={{ height: `${Math.max((row.userCount / maxCreatedHourly) * 135, 2)}px` }}>
                    <div className={styles.BarTooltip}>
                      <strong>{formatNumber(row.userCount)}</strong>
                      <span>{formatHour(row.hour)}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.BarLabel}>{formatHourLabel(row.hour)}</div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.Panel}>
          <div className={styles.PanelHeadRow}>
            <div>
              <h2 className={styles.PanelTitle}>QR tạo mới</h2>
              <p className={styles.PanelHint}>Xu hướng tạo mã theo giờ.</p>
            </div>
            <span className={`${styles.TotalBadge} ${styles.TotalBadgeAmber}`}>
              {formatNumber(totalQrCreated)} QR
            </span>
          </div>
          <div className={styles.Chart}>
            {qrCreatedTotals.length === 0 && <span className={styles.Muted}>Chưa có dữ liệu.</span>}
            {qrCreatedTotals.map((row) => (
              <div className={styles.BarWrap} key={row.hour}>
                <div className={styles.BarTrack}>
                  <div className={styles.BarAmber} style={{ height: `${Math.max((row.qrCodeCount / maxCreatedHourly) * 135, 2)}px` }}>
                    <div className={styles.BarTooltip}>
                      <strong>{formatNumber(row.qrCodeCount)}</strong>
                      <span>{formatHour(row.hour)}</span>
                      <span>Dynamic: {formatNumber(row.dynamicCount)}</span>
                      <span>Static: {formatNumber(row.staticCount)}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.BarLabel}>{formatHourLabel(row.hour)}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className={styles.CreatedStatsSection}>
        <div className={styles.SectionHeader}>
          <div>
            <span className={styles.Eyebrow}>QR creation analytics</span>
            <h2 className={styles.SectionTitle}>Phân tích QR được tạo</h2>
            <p className={styles.SectionHint}>Tách riêng theo loại nội dung và quốc gia tạo QR.</p>
          </div>
          <span className={`${styles.TotalBadge} ${styles.TotalBadgeAmber}`}>
            {formatNumber(totalQrCreated)} QR mới
          </span>
        </div>

        <div className={styles.CreatedBreakdownGrid}>
          <div className={styles.BreakdownCard}>
            <div className={styles.BreakdownHeader}>
              <div>
                <h3>Theo loại</h3>
                <p>Product và content type</p>
              </div>
              <span>{formatNumber(qrCreatedByType.length)} nhóm</span>
            </div>
            <div className={styles.BreakdownList}>
              {qrCreatedByType.length === 0 && <div className={styles.EmptyState}>Chưa có dữ liệu.</div>}
              {qrCreatedByType.slice(0, 10).map((row) => {
                const label = createdTypeLabel(row.type);
                return (
                  <div className={styles.BreakdownRow} key={row.type}>
                    <div className={styles.BreakdownRowHead}>
                      <div className={styles.TypeName}>
                        <span className={styles.TypeBadge}>{label.productLabel}</span>
                        <span title={label.contentLabel}>{label.contentLabel}</span>
                      </div>
                      <strong>{formatNumber(row.count)}</strong>
                    </div>
                    <div className={styles.ProgressTrack}>
                      <div
                        className={styles.ProgressBarType}
                        style={{ width: `${Math.max((row.count / maxCreatedTypeCount) * 100, 2)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.BreakdownCard}>
            <div className={styles.BreakdownHeader}>
              <div>
                <h3>Theo quốc gia</h3>
                <p>Vị trí người tạo QR</p>
              </div>
              <span>{formatNumber(qrCreatedByCountry.length)} quốc gia</span>
            </div>
            <div className={styles.BreakdownList}>
              {qrCreatedByCountry.length === 0 && <div className={styles.EmptyState}>Chưa có dữ liệu.</div>}
              {qrCreatedByCountry.slice(0, 10).map((row) => {
                const normalizedCountry = row.country.trim().toLowerCase();
                const isUnknown = !normalizedCountry || normalizedCountry === "unknown";
                return (
                  <div className={styles.BreakdownRow} key={row.country}>
                    <div className={styles.BreakdownRowHead}>
                      <div className={styles.CountryName}>
                        <span className={styles.CountryFlag}>
                          {isUnknown ? "—" : countryFlagEmoji(normalizedCountry)}
                        </span>
                        <span>{isUnknown ? "Chưa xác định" : normalizedCountry.toUpperCase()}</span>
                      </div>
                      <strong>{formatNumber(row.count)}</strong>
                    </div>
                    <div className={styles.ProgressTrack}>
                      <div
                        className={styles.ProgressBarCountry}
                        style={{ width: `${Math.max((row.count / maxCreatedCountryCount) * 100, 2)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {!createdDimensions && createdCodes.length > 0 && (
              <p className={styles.DataFallbackNote}>Tạm tính từ 100 QR mới nhất do API tổng hợp chưa sẵn sàng.</p>
            )}
          </div>
        </div>
      </section>

      <section className={`${styles.Panel} ${styles.TablePanel}`}>
        <div className={styles.PanelHeadRow}>
          <div>
            <h2 className={styles.PanelTitle}>
              {codeListMode === "scan" ? "QR được scan nhiều nhất" : "QR được tạo gần đây"}
            </h2>
            <p className={styles.PanelHint}>
              {codeListMode === "scan"
                ? "Xếp hạng theo lượt scan trong khoảng thời gian đã lọc."
                : "Danh sách tối đa 100 QR, sắp xếp mới nhất trước."}
            </p>
          </div>
          <div className={styles.ModeToggle}>
            <button
              type="button"
              className={codeListMode === "scan" ? styles.ModeButtonActive : styles.ModeButton}
              aria-pressed={codeListMode === "scan"}
              onClick={() => setCodeListMode("scan")}
            >
              Top scan
            </button>
            <button
              type="button"
              className={codeListMode === "created" ? styles.ModeButtonActive : styles.ModeButton}
              aria-pressed={codeListMode === "created"}
              onClick={() => setCodeListMode("created")}
            >
              QR tạo mới
            </button>
          </div>
        </div>
        <div className={styles.TableWrap}>
          {codeListMode === "scan" ? (
            <Table size="small" className={styles.DataTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Dynamic ID</TableCell>
                  <TableCell>Owner user</TableCell>
                  <TableCell>Quốc gia tạo</TableCell>
                  <TableCell>Lượt scan</TableCell>
                  <TableCell>Ads / No ads</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Giờ scan cuối</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topCodes.map((row) => (
                  <TableRow
                    hover
                    key={row.dynamicId}
                    className={styles.ClickableRow}
                    onClick={() => openQrDetailModal(row.dynamicId)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className={styles.CodeIdCell}>
                        <a
                          className={styles.CodeLink}
                          href={publicQrCodeUrl(row.dynamicId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.dynamicId}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>{row.ownerUserId}</TableCell>
                    <TableCell><CreateCountryCell geo={row.createGeo} /></TableCell>
                    <TableCell>{formatNumber(row.scanCount)}</TableCell>
                    <TableCell>{formatNumber(row.adsScanCount)} / {formatNumber(row.noAdsScanCount)}</TableCell>
                    <TableCell>{(row.domains || []).join(", ")}</TableCell>
                    <TableCell>{formatHour(row.lastScanHour)}</TableCell>
                  </TableRow>
                ))}
                {topCodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className={styles.Muted}>Chưa có dữ liệu.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <Table size="small" className={styles.DataTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Dynamic ID</TableCell>
                  <TableCell>Owner user</TableCell>
                  <TableCell>Quốc gia tạo</TableCell>
                  <TableCell>Loại</TableCell>
                  <TableCell>Lượt scan</TableCell>
                  <TableCell>Thời gian tạo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {createdCodes.map((row) => (
                  <TableRow
                    hover
                    key={row.dynamicId}
                    className={styles.ClickableRow}
                    onClick={() => openQrDetailModal(row.dynamicId)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className={styles.CodeIdCell}>
                        <a
                          className={styles.CodeLink}
                          href={publicQrCodeUrl(row.dynamicId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.dynamicId}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>{row.ownerUserId}</TableCell>
                    <TableCell><CreateCountryCell geo={row.createGeo} /></TableCell>
                    <TableCell>{`${row.productId} / ${row.contentType}`}</TableCell>
                    <TableCell>{formatNumber(row.scanCount)}</TableCell>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                  </TableRow>
                ))}
                {createdCodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className={styles.Muted}>Chưa có dữ liệu.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
        <p className={styles.TableFootnote}>Nhấp vào một hàng để xem chi tiết; nhấp Dynamic ID để mở trang QR công khai.</p>
      </section>

      <Dialog
        open={detailModalOpen}
        onClose={closeQrDetailModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ className: styles.DetailDialogPaper }}
      >
        <DialogTitle className={styles.DetailDialogTitle}>
          <span>
            Chi tiết QR{" "}
            {selectedDynamicId && (
              <a
                className={styles.CodeLink}
                href={publicQrCodeUrl(selectedDynamicId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {selectedDynamicId}
              </a>
            )}
          </span>
          <IconButton aria-label="đóng" onClick={closeQrDetailModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={styles.DetailDialogBody}>
          {detailLoading && (
            <div className={styles.DetailModalLoading}>
              <CircularProgress size={32} />
              <span className={styles.Muted}>Đang tải chi tiết…</span>
            </div>
          )}
          {!detailLoading && detailError && (
            <div className={styles.DetailModalError}>{detailError}</div>
          )}
          {!detailLoading && detail?.code && (
            <>
              <div className={styles.DetailGrid}>
                <div className={styles.DetailItem}><strong>Dynamic ID</strong>{detail.code.dynamicId}</div>
                <div className={styles.DetailItem}><strong>Product ID</strong>{detail.code.productId || "-"}</div>
                <div className={styles.DetailItem}><strong>Owner user</strong>{detail.code.ownerUserId}</div>
                <div className={styles.DetailItem}><strong>Ownership</strong>{detail.code.ownershipStatus || "-"}</div>
                <div className={styles.DetailItem}><strong>Content type</strong>{detail.code.contentType}</div>
                <div className={styles.DetailItem}><strong>Destination URL</strong>{detail.code.destinationUrl || "-"}</div>
                <div className={styles.DetailItem}><strong>Code redirect</strong>{detail.code.codeRedirect || "-"}</div>
                <div className={styles.DetailItem}><strong>Scan counter</strong>{formatNumber(detail.code.scanCounter)}</div>
                <div className={styles.DetailItem}><strong>Status</strong>{detail.code.active ? "Active" : "Inactive"} / {detail.code.enabled ? "Enabled" : "Disabled"}</div>
                <div className={styles.DetailItem}><strong>Created at</strong>{formatDateTime(detail.code.createdAt)}</div>
                <div className={styles.DetailItem}><strong>Updated at</strong>{formatDateTime(detail.code.updatedAt)}</div>
              </div>

              <h3 className={styles.DetailModalSubtitle}>Scan theo giờ (trong khoảng lọc)</h3>
              <div className={styles.DetailModalTableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Giờ</TableCell>
                      <TableCell>Domain</TableCell>
                      <TableCell align="right">Scan</TableCell>
                      <TableCell align="right">Ads</TableCell>
                      <TableCell align="right">No ads</TableCell>
                      <TableCell align="right">Chưa phân loại</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.hourly || []).map((row) => (
                      <TableRow key={`${row.hour}-${row.scanDomain}`}>
                        <TableCell>{formatHour(row.hour)}</TableCell>
                        <TableCell>{row.scanDomain}</TableCell>
                        <TableCell align="right">{formatNumber(row.scanCount)}</TableCell>
                        <TableCell align="right">{formatNumber(row.adsScanCount)}</TableCell>
                        <TableCell align="right">{formatNumber(row.noAdsScanCount)}</TableCell>
                        <TableCell align="right">{formatNumber(row.unknownAdsScanCount)}</TableCell>
                      </TableRow>
                    ))}
                    {(detail.hourly || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className={styles.Muted}>Chưa có dữ liệu scan theo giờ trong khoảng thời gian đã chọn.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <h3 className={styles.DetailModalSubtitle}>Theo domain (trong khoảng lọc)</h3>
              <div className={styles.DetailModalTableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Domain</TableCell>
                      <TableCell align="right">Scan</TableCell>
                      <TableCell align="right">Ads</TableCell>
                      <TableCell align="right">No ads</TableCell>
                      <TableCell align="right">Chưa phân loại</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.domains || []).map((row) => (
                      <TableRow key={row.scanDomain}>
                        <TableCell>{row.scanDomain}</TableCell>
                        <TableCell align="right">{formatNumber(row.scanCount)}</TableCell>
                        <TableCell align="right">{formatNumber(row.adsScanCount)}</TableCell>
                        <TableCell align="right">{formatNumber(row.noAdsScanCount)}</TableCell>
                        <TableCell align="right">{formatNumber(row.unknownAdsScanCount)}</TableCell>
                      </TableRow>
                    ))}
                    {(detail.domains || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className={styles.Muted}>Chưa có dữ liệu theo domain.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          {!detailLoading && !detailError && detail && !detail.code && (
            <div className={styles.Muted}>Không tìm thấy QR code trong user_codes.</div>
          )}
        </DialogContent>
        <DialogActions className={styles.DetailDialogActions}>
          {detail?.code?.ownershipStatus === "anonymous" && detail.code.dynamicId && (
            <button
              type="button"
              className={styles.SecondaryButton}
              onClick={() => {
                setAssignDynamicIds(detail.code?.dynamicId || "");
                closeQrDetailModal();
              }}
            >
              Đưa vào form assign
            </button>
          )}
          <button type="button" className={styles.PrimaryButton} onClick={closeQrDetailModal}>
            Đóng
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default QrScanMonitor;
